/**
 * 맞춤 알림 규칙의 계산 — 정규화 · 기본값 · 라벨 · 요약.
 *
 * ⚠️ **저장소를 치지 않는다.** Firestore 접근은 `actions/firestore/sdk/alert-rule` 이 한다.
 * 이 파일이 순수하므로 서버가 계산 하나를 빌려도 클라이언트 SDK가 딸려 오지 않는다.
 *
 * 어휘(코드 값·선택지)는 `constants/alert-rule` · 모양은 `types/alert-rule/alert-rule-domain`.
 */

import type {
  AlertContent,
  AlertRuleDraft,
  AlertRuleFilter,
  AlertSourceType,
  AlertScheduleMode,
  AlertConditionItem,
  AlertConditionType,
  AlertSlackConnection,
  AlertLegacyCondition,
} from 'src/types/alert-rule/alert-rule-domain';

import {
  ALERT_SOURCE_TYPES,
  ALERT_CONTENT_TYPES,
  ALERT_SCHEDULE_MODES,
  ALERT_SOURCE_OPTIONS,
  ALERT_CONTENT_OPTIONS,
  ALERT_CONDITION_TYPES,
  ALERT_SCHEDULE_OPTIONS,
  ALERT_CONDITION_OPTIONS,
  ALERT_CAMPAIGN_COLLECTIONS,
} from 'src/constants/alert-rule';

/**
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeAlertMetricMin(value: unknown): number {
  if (value === '' || value === null || value === undefined) return 0
  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) return 0
  return Math.floor(num)
}

/**
 * E/R 퍼센트 (소수 허용, 소수 2자리)
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeAlertErMin(value: unknown): number {
  if (value === '' || value === null || value === undefined) return 0
  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) return 0
  return Math.round(num * 100) / 100
}

/**
 * @param {AlertConditionType} type
 * @returns {AlertConditionItem|null}
 */
export function createEmptyAlertCondition(type: AlertConditionType): AlertConditionItem | null {
  const t = String(type || '').trim()
  if (t === ALERT_CONDITION_TYPES.METRIC_THRESHOLD) {
    return { type: t as AlertConditionType, params: { metricMin: 0 } }
  }
  if (t === ALERT_CONDITION_TYPES.VIEW_COUNT) {
    return { type: t as AlertConditionType, params: { viewMin: 0 } }
  }
  if (t === ALERT_CONDITION_TYPES.ENGAGEMENT_RATE) {
    return { type: t as AlertConditionType, params: { erMin: 0 } }
  }
  return null
}

/**
 * @param {Object|null|undefined} raw
 * @returns {AlertConditionItem|null}
 */
function normalizeAlertConditionItem(raw: any): AlertConditionItem | null {
  const type = String(raw?.type || '').trim() as AlertConditionType
  const params = raw?.params || {}
  if (type === ALERT_CONDITION_TYPES.METRIC_THRESHOLD) {
    return { type, params: { metricMin: normalizeAlertMetricMin(params.metricMin) } }
  }
  if (type === ALERT_CONDITION_TYPES.VIEW_COUNT) {
    return { type, params: { viewMin: normalizeAlertMetricMin(params.viewMin) } }
  }
  if (type === ALERT_CONDITION_TYPES.ENGAGEMENT_RATE) {
    return { type, params: { erMin: normalizeAlertErMin(params.erMin) } }
  }
  return null
}

/**
 * 규칙/드래프트 → AND 조건 배열. 빈 배열 = 신규 포스팅 전체.
 * 레거시 `condition` 단일 객체도 읽는다.
 * @param {Object|null|undefined} ruleOrDraft
 * @returns {AlertConditionItem[]}
 */
export function normalizeAlertConditions(ruleOrDraft: any): AlertConditionItem[] {
  const rawList = Array.isArray(ruleOrDraft?.conditions) ? ruleOrDraft.conditions : null
  if (rawList) {
    const seen = new Set()
    const next: AlertConditionItem[] = []
    rawList.forEach((item: any) => {
      const normalized = normalizeAlertConditionItem(item)
      if (!normalized || seen.has(normalized.type)) return
      seen.add(normalized.type)
      next.push(normalized)
    })
    return next
  }

  const legacy = ruleOrDraft?.condition
  if (!legacy || typeof legacy !== 'object') return []
  if (String(legacy.type || '').trim() === ALERT_CONDITION_TYPES.ALL_NEW_POSTS) return []
  const item = normalizeAlertConditionItem(legacy)
  return item ? [item] : []
}

/**
 * CF 레거시 호환용 단일 condition (conditions 비어 있으면 all_new_posts)
 * @param {AlertConditionItem[]} conditions
 * @returns {AlertLegacyCondition}
 */
export function toLegacyAlertCondition(conditions: AlertConditionItem[] = []): AlertLegacyCondition {
  const list = Array.isArray(conditions) ? conditions : []
  if (!list.length) {
    return { type: ALERT_CONDITION_TYPES.ALL_NEW_POSTS as AlertConditionType, params: { metricMin: 0 } }
  }
  const metric = list.find((c) => c.type === ALERT_CONDITION_TYPES.METRIC_THRESHOLD)
  if (metric) return normalizeAlertConditionItem(metric) as AlertLegacyCondition
  return normalizeAlertConditionItem(list[0]) || {
    type: ALERT_CONDITION_TYPES.ALL_NEW_POSTS as AlertConditionType,
    params: { metricMin: 0 },
  }
}

/**
 * @param {Object|null|undefined} raw
 * @returns {{ platforms: string[], markets: string[] }}
 */
export function normalizeAlertRuleFilter(raw: any): AlertRuleFilter {
  const platforms: string[] = Array.isArray(raw?.platforms)
    ? raw.platforms.map((v: any) => String(v || '').trim().toLowerCase()).filter(Boolean)
    : []
  const markets: string[] = Array.isArray(raw?.markets)
    ? raw.markets.map((v: any) => String(v || '').trim().toUpperCase()).filter(Boolean)
    : []
  return {
    platforms: [...new Set(platforms)],
    markets: [...new Set(markets)],
  }
}

function createDefaultAlertRuleFilter() {
  return normalizeAlertRuleFilter({})
}

/**
 * @param {Object|null|undefined} filter
 */
export function formatAlertFilterSummary(filter: any): string {
  const { platforms, markets } = normalizeAlertRuleFilter(filter)
  const channelPart = platforms.length ? `채널 ${platforms.length}개` : '채널 전체'
  const marketPart = markets.length ? `국가 ${markets.length}개` : '국가 전체'
  return `${channelPart} · ${marketPart}`
}

/**
 * @param {Object|null|undefined} filter
 * @param {{ id: string, label?: string }[]} platformOptions
 */
export function getAlertFilterPlatformLabels(filter: any, platformOptions: any[] = []): string[] {
  const { platforms } = normalizeAlertRuleFilter(filter)
  if (!platforms.length) return []
  const labelById = new Map(
    (Array.isArray(platformOptions) ? platformOptions : []).map((p): [string, string] => [
      String(p.id || '').toLowerCase(),
      String(p.label || p.id || ''),
    ]),
  )
  return platforms.map((id) => labelById.get(id) || id)
}

/**
 * @param {Object|null|undefined} filter
 * @param {{ code: string, ko?: string, country?: string }[]} countryOptions
 */
export function getAlertFilterMarketLabels(filter: any, countryOptions: any[] = []): string[] {
  const { markets } = normalizeAlertRuleFilter(filter)
  if (!markets.length) return []
  const labelByCode = new Map(
    (Array.isArray(countryOptions) ? countryOptions : []).map((c): [string, string] => {
      const code = String(c.code || '').toUpperCase()
      return [code, String(c.ko || c.country || code)]
    }),
  )
  return markets.map((code) => labelByCode.get(code) || code)
}

/**
 * @returns {Record<string, boolean>}
 */
function createDefaultAlertContent(): AlertContent {
  return {
    [ALERT_CONTENT_TYPES.POSTING_LIST]: true,
    [ALERT_CONTENT_TYPES.STATISTICS]: false,
    [ALERT_CONTENT_TYPES.AI_SUMMARY]: false,
    [ALERT_CONTENT_TYPES.MENTIONED_BRANDS]: false,
    [ALERT_CONTENT_TYPES.NEGATIVE_MENTIONS]: false,
  }
}

/**
 * @param {Object|null|undefined} raw
 * @returns {Record<string, boolean>}
 */
export function normalizeAlertContent(raw: any): AlertContent {
  const defaults = createDefaultAlertContent()
  if (!raw || typeof raw !== 'object') return defaults
  const next = { ...defaults }
  ALERT_CONTENT_OPTIONS.forEach((option) => {
    next[option.value] = raw[option.value] === true
  })
  return next
}

/** 실시간 주기 — 포스팅 리스트만 허용 */
function createRealtimeAlertContent(): AlertContent {
  return {
    [ALERT_CONTENT_TYPES.POSTING_LIST]: true,
    [ALERT_CONTENT_TYPES.STATISTICS]: false,
    [ALERT_CONTENT_TYPES.AI_SUMMARY]: false,
    [ALERT_CONTENT_TYPES.MENTIONED_BRANDS]: false,
    [ALERT_CONTENT_TYPES.NEGATIVE_MENTIONS]: false,
  }
}

export function isRealtimeAlertSchedule(scheduleMode: any): boolean {
  return String(scheduleMode || '').trim() === ALERT_SCHEDULE_MODES.REALTIME
}

/**
 * @param {string} scheduleMode
 * @param {Object|null|undefined} raw
 * @returns {AlertContent}
 */
export function normalizeAlertContentForSchedule(scheduleMode: any, raw: any): AlertContent {
  if (isRealtimeAlertSchedule(scheduleMode)) return createRealtimeAlertContent()
  return normalizeAlertContent(raw)
}

/**
 * @param {Object|null|undefined} content
 * @returns {boolean}
 */
export function hasAnyAlertContentEnabled(content: any): boolean {
  return ALERT_CONTENT_OPTIONS.some((option) => normalizeAlertContent(content)[option.value])
}

/**
 * @param {Object|null|undefined} content
 * @returns {string[]}
 */
export function getAlertContentLabels(content: any): string[] {
  const normalized = normalizeAlertContent(content)
  return ALERT_CONTENT_OPTIONS
    .filter((option) => normalized[option.value])
    .map((option) => option.label)
}

/**
 * @param {import('firebase/firestore').Timestamp|Date|string|number|null|undefined} ts
 * @returns {Date|null}
 */
export function alertTimestampToDate(ts: any): Date | null {
  if (!ts) return null
  if (typeof ts?.toDate === 'function') return ts.toDate()
  const date = ts instanceof Date ? ts : new Date(ts)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * ⚠️ `AlertSourceType`이 아니라 `string`을 받는다 — 목록에서 찾는 함수라 무엇이 와도
 * 안전하고, 좁히면 저장 문서에서 읽은 값(문자열)을 넘길 때 호출부가 단언을 하게 된다.
 * @param {string} sourceType
 * @returns {string}
 */
export function getAlertSourceCampaignCollection(sourceType: string): string {
  const option = ALERT_SOURCE_OPTIONS.find((o) => o.value === sourceType)
  return option?.campaignCollection || ''
}

/**
 * ⚠️ 넘어온 배열을 **그대로** 돌려준다 — 그 안이 무엇인지 이 파일은 모른다.
 *    화면이 `campaign.id` · `campaign.name` 을 읽으므로 `object[]` 로 닫지 않는다.
 *
 * @param {AlertSourceType} sourceType
 * @param {{ keywordCampaigns?: Record<string, any>[], seedingCampaigns?: Record<string, any>[] }} [lists]
 * @returns {Record<string, any>[]}
 */
export function getCampaignsForAlertSource(sourceType: any, { keywordCampaigns = [], seedingCampaigns = [] }: { keywordCampaigns?: any[]; seedingCampaigns?: any[] } = {}): Record<string, any>[] {
  if (sourceType === ALERT_SOURCE_TYPES.KEYWORD_MONITORING) {
    return Array.isArray(keywordCampaigns) ? keywordCampaigns : []
  }
  if (sourceType === ALERT_SOURCE_TYPES.SEEDING_CAMPAIGN) {
    return Array.isArray(seedingCampaigns) ? seedingCampaigns : []
  }
  return []
}

/**
 * @param {AlertSourceType} sourceType
 * @param {{ workspace?: Record<string, any>|null, keywordCampaigns?: Record<string, any>[], seedingCampaigns?: Record<string, any>[] }} [lists]
 */
export function isAlertSourceReady(sourceType: any, { workspace, keywordCampaigns = [], seedingCampaigns = [] }: { workspace?: any; keywordCampaigns?: any[]; seedingCampaigns?: any[] } = {}): boolean {
  if (sourceType === ALERT_SOURCE_TYPES.BRAND_MENTION) {
    return hasBrandMentionSourceReady(workspace)
  }
  if (sourceType === ALERT_SOURCE_TYPES.KEYWORD_MONITORING) {
    return getCampaignsForAlertSource(sourceType, { keywordCampaigns }).length > 0
  }
  if (sourceType === ALERT_SOURCE_TYPES.SEEDING_CAMPAIGN) {
    return getCampaignsForAlertSource(sourceType, { seedingCampaigns }).length > 0
  }
  return false
}

/**
 * @param {string} campaignId
 * @param {string} campaignCollection
 * @param {{ keywordCampaigns?: object[], seedingCampaigns?: object[] }} lists
 */
export function resolveAlertCampaignName(
  campaignId: any,
  campaignCollection: any,
  { keywordCampaigns = [], seedingCampaigns = [] }: { keywordCampaigns?: any[]; seedingCampaigns?: any[] } = {},
) {
  const id = String(campaignId || '').trim()
  if (!id) return null
  const list = campaignCollection === ALERT_CAMPAIGN_COLLECTIONS.SEEDING_CAMPAIGN
    ? seedingCampaigns
    : keywordCampaigns
  const found = (Array.isArray(list) ? list : []).find((c) => String(c?.id || '') === id)
  return found?.name || id
}

/**
 * @param {Object|null|undefined} workspace
 */
function hasBrandMentionSourceReady(workspace: any): boolean {
  const keywords = workspace?.brandSetting?.brandKeyword
  return Array.isArray(keywords) && keywords.some((k) => String(k || '').trim())
}

/**
 * @param {AlertSourceType} sourceType
 */
function getAlertSourceLabel(sourceType: any) {
  return ALERT_SOURCE_OPTIONS.find((o) => o.value === sourceType)?.label ?? sourceType
}

/**
 * @param {AlertConditionType} conditionType
 */
function getAlertConditionLabel(conditionType: any) {
  if (conditionType === ALERT_CONDITION_TYPES.ALL_NEW_POSTS) return '신규 전체'
  return ALERT_CONDITION_OPTIONS.find((o) => o.value === conditionType)?.label ?? conditionType
}

/**
 * 단일 조건 요약
 * @param {Object|null|undefined} condition
 */
function formatAlertConditionItemSummary(condition: any) {
  const type = condition?.type
  const params = condition?.params || {}
  if (type === ALERT_CONDITION_TYPES.METRIC_THRESHOLD) {
    const min = params.metricMin ?? 0
    return min > 0 ? `도달 ${min.toLocaleString()} 이상` : '도달 수치'
  }
  if (type === ALERT_CONDITION_TYPES.VIEW_COUNT) {
    const min = params.viewMin ?? 0
    return min > 0 ? `조회수 ${min.toLocaleString()}개 이상` : '조회수'
  }
  if (type === ALERT_CONDITION_TYPES.ENGAGEMENT_RATE) {
    const min = params.erMin ?? 0
    return min > 0 ? `E/R ${min}% 이상` : 'E/R'
  }
  if (type === ALERT_CONDITION_TYPES.ALL_NEW_POSTS) return '신규 포스팅 전체'
  return getAlertConditionLabel(type)
}

/**
 * 카드·칩 등에 표시할 조건 요약 (AND 결합)
 * @param {Object|Object[]|null|undefined} conditionOrRule
 *   - 조건 배열 / 단일 condition / rule·draft(conditions|condition 포함)
 */
export function formatAlertConditionSummary(conditionOrRule: any): string {
  if (Array.isArray(conditionOrRule)) {
    if (!conditionOrRule.length) return '신규 포스팅 전체'
    return conditionOrRule.map(formatAlertConditionItemSummary).join(' · ')
  }

  if (conditionOrRule && typeof conditionOrRule === 'object') {
    if (Array.isArray(conditionOrRule.conditions) || conditionOrRule.condition) {
      const list = normalizeAlertConditions(conditionOrRule)
      if (!list.length) return '신규 포스팅 전체'
      return list.map(formatAlertConditionItemSummary).join(' · ')
    }
    return formatAlertConditionItemSummary(conditionOrRule)
  }

  return '신규 포스팅 전체'
}

/**
 * @param {AlertScheduleMode} scheduleMode
 */
export function getAlertScheduleLabel(scheduleMode: any) {
  return ALERT_SCHEDULE_OPTIONS.find((o) => o.value === scheduleMode)?.label ?? scheduleMode
}

/**
 * @param {Object|null|undefined} rule
 */
export function buildAlertRuleSummary(rule: any): string {
  if (!rule) return ''
  const parts = [
    getAlertSourceLabel(rule.source?.type),
    formatAlertConditionSummary(rule),
    getAlertScheduleLabel(rule.schedule?.mode),
  ].filter(Boolean)
  return parts.join(' · ')
}

/**
 * @param {Object|null|undefined} slack
 * @param {string} [slackWebhookUrl]
 * @returns {string}
 */
export function resolveSlackWebhookUrl(slack: any, slackWebhookUrl = ''): string {
  return String(slack?.webhookUrl || slackWebhookUrl || '').trim()
}

/**
 * @param {Object|null|undefined} slack
 * @param {string} [slackWebhookUrl]
 * @returns {AlertSlackConnection|null}
 */
export function normalizeSlackConnectionPayload(slack: any, slackWebhookUrl = ''): AlertSlackConnection | null {
  const webhookUrl = resolveSlackWebhookUrl(slack, slackWebhookUrl)
  if (!webhookUrl) return null

  return {
    accessToken: String(slack?.accessToken || '').trim(),
    teamId: String(slack?.teamId || '').trim(),
    teamName: String(slack?.teamName || '').trim(),
    channelId: String(slack?.channelId || '').trim(),
    channelName: String(slack?.channelName || '').trim(),
    botUserId: String(slack?.botUserId || '').trim(),
    webhookUrl,
    installedAt: slack?.installedAt || new Date().toISOString(),
  }
}

/**
 * @param {string} channelName
 * @param {string} sourceLabel
 */
function formatWorkspaceSlackChannelLabel(channelName: any, sourceLabel: any): string {
  const channel = String(channelName || '').trim()
  const normalizedChannel = channel
    ? (channel.startsWith('#') ? channel : `#${channel}`)
    : '채널 미지정'
  const source = String(sourceLabel || '').trim()
  return source ? `${normalizedChannel} · ${source}` : normalizedChannel
}

/**
 * @param {{
 *   workspace?: Object|null,
 *   keywordCampaigns?: Object[],
 *   seedingCampaigns?: Object[],
 *   alertRules?: Object[],
 *   excludeRuleId?: string,
 * }} params
 * ⚠️ 결과의 `slack` 은 연결 정보 객체이거나 `null` 이다 — 속성이 하나도 없는 `Object` 로
 *    좁히면 소비처가 막힌다.
 *
 * @returns {Array<{ key: string, webhookUrl: string, slack: Record<string, any>|null, label: string, sourceType: string, sourceId: string, sourceName: string }>}
 */
export function collectWorkspaceSlackChannelOptions({
  workspace,
  keywordCampaigns = [],
  seedingCampaigns = [],
  alertRules = [],
  excludeRuleId = '',
}: any = {}) {
  const excludeId = String(excludeRuleId || '').trim()
  const byWebhook = new Map()

  const pushOption = (entry: any) => {
    if (!entry?.webhookUrl || byWebhook.has(entry.webhookUrl)) return
    byWebhook.set(entry.webhookUrl, entry)
  }

  const workspaceSlack = normalizeSlackConnectionPayload(
    workspace?.slack,
    workspace?.slackUrl,
  )
  if (workspaceSlack) {
    pushOption({
      key: workspaceSlack.webhookUrl,
      webhookUrl: workspaceSlack.webhookUrl,
      slack: workspaceSlack,
      sourceType: 'workspace',
      sourceId: String(workspace?.id || workspace?.workspaceId || '').trim(),
      sourceName: '워크스페이스',
      label: formatWorkspaceSlackChannelLabel(workspaceSlack.channelName, '워크스페이스'),
    })
  }

  ; (Array.isArray(keywordCampaigns) ? keywordCampaigns : []).forEach((campaign) => {
    const slack = normalizeSlackConnectionPayload(campaign?.slack, campaign?.slackWebhookUrl)
    if (!slack) return
    pushOption({
      key: slack.webhookUrl,
      webhookUrl: slack.webhookUrl,
      slack,
      sourceType: 'keyword_campaign',
      sourceId: String(campaign?.id || '').trim(),
      sourceName: String(campaign?.name || '키워드 캠페인').trim(),
      label: formatWorkspaceSlackChannelLabel(
        slack.channelName,
        `키워드 · ${String(campaign?.name || '캠페인').trim()}`,
      ),
    })
  })

    ; (Array.isArray(seedingCampaigns) ? seedingCampaigns : []).forEach((campaign) => {
      const slack = normalizeSlackConnectionPayload(campaign?.slack, campaign?.slackWebhookUrl)
      if (!slack) return
      pushOption({
        key: slack.webhookUrl,
        webhookUrl: slack.webhookUrl,
        slack,
        sourceType: 'seeding_campaign',
        sourceId: String(campaign?.id || '').trim(),
        sourceName: String(campaign?.name || '시딩 캠페인').trim(),
        label: formatWorkspaceSlackChannelLabel(
          slack.channelName,
          `시딩 · ${String(campaign?.name || '캠페인').trim()}`,
        ),
      })
    })

    ; (Array.isArray(alertRules) ? alertRules : []).forEach((rule) => {
      const ruleId = String(rule?.id || '').trim()
      if (excludeId && ruleId === excludeId) return
      const slack = normalizeSlackConnectionPayload(rule?.slack, rule?.slackWebhookUrl)
      if (!slack) return
      pushOption({
        key: slack.webhookUrl,
        webhookUrl: slack.webhookUrl,
        slack,
        sourceType: 'alert_rule',
        sourceId: ruleId,
        sourceName: String(rule?.name || '맞춤 알림').trim(),
        label: formatWorkspaceSlackChannelLabel(
          slack.channelName,
          `맞춤알림 · ${String(rule?.name || '알림').trim()}`,
        ),
      })
    })

  return Array.from(byWebhook.values())
}

/**
 * @param {Object|null|undefined} rule
 * @returns {boolean}
 */
export function isAlertRuleSlackConnected(rule: any): boolean {
  return Boolean(resolveSlackWebhookUrl(rule?.slack, rule?.slackWebhookUrl))
}

/**
 * @param {Object|null|undefined} rule
 * @returns {string|null}
 */
export function getAlertRuleSlackChannelLabel(rule: any): string | null {
  const channelName = String(rule?.slack?.channelName || '').trim()
  if (channelName) return channelName.startsWith('#') ? channelName : `#${channelName}`
  const teamName = String(rule?.slack?.teamName || '').trim()
  if (teamName) return teamName
  return null
}

/**
 * 새 규칙 편집을 시작할 때의 초기 draft.
 * @returns {AlertRuleDraft}
 */
export function createEmptyAlertRuleDraft(): AlertRuleDraft {
  return {
    name: '',
    enabled: true,
    source: {
      type: ALERT_SOURCE_TYPES.BRAND_MENTION as AlertSourceType,
      campaignId: '',
      campaignCollection: '',
    },
    /** AND 조건 목록. 빈 배열 = 신규 포스팅 전체 */
    conditions: [],
    schedule: {
      mode: ALERT_SCHEDULE_MODES.DAILY as AlertScheduleMode,
      timezone: 'Asia/Seoul',
      hour: 9,
      dayOfWeek: 1,
    },
    channels: {
      email: { enabled: true, recipientUserIds: [] },
      slack: { enabled: false },
    },
    content: createDefaultAlertContent(),
    filter: createDefaultAlertRuleFilter(),
    slack: null,
    slackWebhookUrl: '',
  }
}

/** 저장된 규칙 → 폼 초안 — 저장본에 빠진 칸은 기본값으로 채운다 */
export function ruleToDraft(rule: Record<string, any> | null | undefined): AlertRuleDraft {
  if (!rule) return createEmptyAlertRuleDraft()
  return {
    name: rule.name || '',
    enabled: rule.enabled !== false,
    source: {
      type: rule.source?.type || ALERT_SOURCE_TYPES.BRAND_MENTION,
      campaignId: rule.source?.campaignId || '',
      campaignCollection: rule.source?.campaignCollection || getAlertSourceCampaignCollection(rule.source?.type),
    },
    conditions: normalizeAlertConditions(rule),
    schedule: {
      mode: rule.schedule?.mode || ALERT_SCHEDULE_MODES.DAILY,
      timezone: rule.schedule?.timezone || 'Asia/Seoul',
      hour: rule.schedule?.hour ?? 9,
      dayOfWeek: rule.schedule?.dayOfWeek ?? 1,
    },
    channels: {
      email: {
        enabled: rule.channels?.email?.enabled === true,
        recipientUserIds: Array.isArray(rule.channels?.email?.recipientUserIds) ? rule.channels.email.recipientUserIds : [],
      },
      slack: { enabled: rule.channels?.slack?.enabled === true },
    },
    content: normalizeAlertContentForSchedule(rule.schedule?.mode, rule.content),
    filter: normalizeAlertRuleFilter(rule.filter),
    slack: rule.slack || null,
    slackWebhookUrl: rule.slackWebhookUrl || '',
  }
}
